import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "config/axiosConfig";
import { Team } from "util/constants";
import TeamProfilePlate from "components/Teams/TeamProfilePlate";
import UserProfilePlate from "components/User/UserProfilePlate";
import LoadingSkeleton from "ui/LoadingSkeleton";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import { AxiosError } from "axios";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import { useUser } from "util/UserContext";
import { apiRoutes } from "util/apiRoutes";

// Define Actor interface that combines both Team and User
interface Actor {
  id: number;
  actor_type: "user" | "team";
  actor_name: string;
  actor_display_name: string;
  details: any;
  properties_public: any;
  about: any;
  viewer_relation: string;
  created_at: string;
  updated_at: string;
}

// Props interface for the ActorsList component
interface Props {
  isLoading: (isLoading: boolean) => void;
  actorTypes?: ("user" | "team")[];
  excludeIds?: number[];
  includeIds?: number[];
}

/**
 * ActorsList component that displays both teams and users
 * Fetches data from apiRoutes.actors.root API with pagination support
 */
const ActorsList: React.FC<Props> = ({
  isLoading,
  actorTypes = ["user", "team"],
  excludeIds = [],
  includeIds = [],
}) => {
  const { awaitingUser } = useUser();
  const [actors, setActors] = useState<Actor[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  /**
   * Fetches actors from the API with pagination
   * @param page - The page number to fetch
   */
  const fetchActors = useCallback(
    async (page: number) => {
      try {
        setLoading(true);


        const response = await api.get(apiRoutes.actors.root, {
            params: {
                limit: 5,
                offset: (page - 1) * 5,
                actorType: actorTypes.join(","),
            },
      });

        if (response.data.length < 5) {
          setHasMore(false);
        }

        setActors((prevActors) => {
          const newActors = response.data.filter(
            (newActor: Actor) =>
              !prevActors.some((actor) => actor.id === newActor.id)
          );
          return [...prevActors, ...newActors];
        });
        
        setLoading(false);
        isLoading(false);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          setError("404: Failed to fetch actors");
        } else {
          setError("Failed to fetch actors");
        }
        setLoading(false);
        isLoading(false);
      }
    },
    [isLoading, actorTypes]
  );

  // Initial load of actors
  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    setActors([]);
    setPage(1);
    setHasMore(true);
    fetchActors(1);
  }, [fetchActors, awaitingUser]);

  // Load more actors when page changes
  useEffect(() => {
    if (page > 1) {
      fetchActors(page);
    }
  }, [page, fetchActors]);

  // Intersection Observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const lastActorElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        }
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Show loading skeleton on initial load
  if (loading && page === 1) {
    return <LoadingSkeleton variant="actor-card" avatarSize="h-16 w-16" count={5} />;
  }

  // Show error page if there's an error
  if (error) {
    return error.match(/404/) ? (
      <NotFoundPage
        displayStyle={`page`}
        errorMessage={`Sorry, we couldn't find any actors.`}
      />
    ) : (
      <ServerErrorPage />
    );
  }

  // Render the actors list
  return (
    <div className="dark:bg-eoe-dark-bg overflow-x-hidden">
      <div className="entity-divider flex flex-col">
        {actors.map((actor, index) => (
          <div
            key={`${actor.actor_type}-${actor.id}`}
            ref={index === actors.length - 1 ? lastActorElementRef : null}
          >
            {actor.actor_type === "team" ? (
              <TeamProfilePlate 
                team={actor.details as unknown as Team} 
              />
            ) : (
              <UserProfilePlate 
                user={{
                  ...actor,
                  id: actor.id,
                  username: actor.actor_name,
                  display_name: actor.actor_display_name,
                  properties_public: actor.properties_public,
                  about: actor.about,
                  viewer_relation: actor.viewer_relation
                } as any} 
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActorsList;
